'use client';
import React, { useState, useEffect } from 'react';
import StepIndicator from './StepIndicator';
import Step1Upload from './Step1Upload';
import Step2Review from './Step2Review';
import Step3Rates from './Step3Rates';
import Step4Preview from './Step4Preview';

export interface ParsedProfile {
  pseudonym: string;
  avatarSeed: string;
  headline: string;
  domainTags: string[];
  sectorExposure: string[];
  experienceTier: 'SENIOR' | 'PRINCIPAL' | 'EXPERT';
  summaryBio: string;
  keyStrengths: string[];
  engagementTypes: string[];
}

export interface RateConfig {
  hourlyRate: number;
  sessionRate: number;
  rushMultiplier: number;
}

const STEPS = ['Upload CV', 'Review Profile', 'Set Rates', 'Preview & Submit'];

export default function OnboardingWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [parsedProfile, setParsedProfile] = useState<ParsedProfile | null>(null);
  const [rateConfig, setRateConfig] = useState<RateConfig>({
    hourlyRate: 0,
    sessionRate: 0,
    rushMultiplier: 1.5,
  });
  const [submitted, setSubmitted] = useState(false);
  const [expertId, setExpertId] = useState<string | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [initLoading, setInitLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const cachedId = localStorage.getItem('ev_expert_id');
      const cachedPseudonym = localStorage.getItem('ev_pseudonym');
      if (cachedId && cachedPseudonym) {
        setExpertId(cachedId);
        setInitLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/expert/init-profile', { method: 'POST' });
        const data = await response.json();
        if (!response.ok || !data.expert_id) {
          setInitError(data.error || 'Profile setup failed. Please refresh and try again.');
          return;
        }
        localStorage.setItem('ev_expert_id', data.expert_id);
        localStorage.setItem('ev_pseudonym', data.pseudonym);
        setExpertId(data.expert_id);
      } catch {
        setInitError('Profile setup failed. Please refresh and try again.');
      } finally {
        setInitLoading(false);
      }
    };
    init();
  }, []);

  const handleNext = () => setCurrentStep((s) => Math.min(s + 1, 3));
  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  if (submitted) {
    const pseudonym =
      parsedProfile?.pseudonym || localStorage.getItem('ev_pseudonym') || 'Advisor #???';
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center gap-6 animate-slide-up">
        <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center">
          <span className="text-3xl">◆</span>
        </div>
        <div>
          <h2 className="font-heading text-display text-foreground mb-3">Profile Submitted</h2>
          <p className="text-muted-foreground max-w-md">
            Your masked profile has been submitted for admin review. You will be notified once your
            profile goes live. Typical review time is 24–48 hours.
          </p>
        </div>
        <div className="surface-elevated p-4 flex flex-col items-center gap-2">
          <span className="font-mono-data text-primary text-lg font-bold">{pseudonym}</span>
          <span className="text-xs text-muted-foreground">
            Save your Advisor Code — you&apos;ll need it to return to your application.
          </span>
        </div>
        <p className="text-xs text-muted-foreground font-mono-data">Status: PENDING REVIEW</p>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="text-center mb-10">
        <p className="text-xs font-mono-data text-primary uppercase tracking-widest mb-2">
          Expert Enrollment
        </p>
        <h1 className="font-heading text-display text-foreground mb-2">Create Your Masked Profile</h1>
        <p className="text-muted-foreground text-sm max-w-lg mx-auto">
          Upload your CV and our AI will extract your expertise while permanently masking your
          identity.
        </p>
      </div>

      <StepIndicator steps={STEPS} currentStep={currentStep} />

      {initError && (
        <div className="mt-6 max-w-2xl mx-auto p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm text-center">
          {initError}
        </div>
      )}

      <div className="mt-10 max-w-2xl mx-auto">
        {currentStep === 0 && (
          <Step1Upload
            expertId={expertId}
            initLoading={initLoading}
            onParsed={(profile) => {
              setParsedProfile(profile);
              localStorage.setItem('ev_pseudonym', profile.pseudonym);
              handleNext();
            }}
          />
        )}
        {currentStep === 1 && parsedProfile && (
          <Step2Review
            profile={parsedProfile}
            onChange={setParsedProfile}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}
        {currentStep === 2 && (
          <Step3Rates
            rates={rateConfig}
            onChange={setRateConfig}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}
        {currentStep === 3 && parsedProfile && (
          <Step4Preview
            expertId={expertId}
            profile={parsedProfile}
            rates={rateConfig}
            onBack={handleBack}
            onSubmit={() => setSubmitted(true)}
          />
        )}
      </div>
    </div>
  );
}
